import { useState, useMemo, useEffect } from 'react';
import SearchableSelect from '../SearchableSelect';
import './ZZPShared.css';
import './ContractFormModal.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const RATE_LABELS = [
  { key: 'bedrag_1', label: 'Bedrag 1 — Ochtenddienst (07:00-15:30)' },
  { key: 'bedrag_2', label: 'Bedrag 2 — Avonddienst (15:30-00:00)' },
  { key: 'bedrag_3', label: 'Bedrag 3 — Nachtdienst (00:00-08:00)' },
  { key: 'bedrag_4', label: 'Bedrag 4 — Overwerk na 8 uren' },
  { key: 'bedrag_5', label: 'Bedrag 5 — Zaterdag uren' },
  { key: 'bedrag_6', label: 'Bedrag 6 — Zondag uren' },
  { key: 'bedrag_7', label: 'Bedrag 7 — Feestdag uren' },
];

const toDateInput = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
};

export default function ContractFormModal({ contract = null, onClose, onSaved }) {
  const isEdit = !!(contract && contract.id);

  const [workers, setWorkers]                 = useState([]);
  const [werkzaamheden, setWerkzaamheden]     = useState([]);
  const [opdrachtgevers, setOpdrachtgevers]   = useState([]);
  const [functiegroepen, setFunctiegroepen]   = useState([]);
  const [werkzaamhedenMode, setWerkzaamhedenMode] = useState('select'); // 'select' | 'other'

  const [form, setForm] = useState(() => ({
    workerId:         contract?.worker_id != null ? String(contract.worker_id) : '',
    opdrachtgeverId:  contract?.opdrachtgever_id != null ? String(contract.opdrachtgever_id) : '',
    project_naam:  contract?.project_naam  ?? '',
    werkzaamheden: contract?.werkzaamheden ?? '',
    datum_in:      toDateInput(contract?.datum_in),
    datum_uit:     toDateInput(contract?.datum_uit),
    tekendatum:    toDateInput(contract?.tekendatum),
  }));
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch dropdown data (workers, werkzaamheden, opdrachtgevers, functiegroepen for preview)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wRes, kRes, oRes, fRes] = await Promise.all([
          authFetch('/api/workers'),
          authFetch('/api/zzp/werkzaamheden'),
          authFetch('/api/zzp/opdrachtgevers'),
          authFetch('/api/zzp/functiegroepen'),
        ]);
        const wData = await wRes.json().catch(() => []);
        const kData = await kRes.json().catch(() => []);
        const oData = await oRes.json().catch(() => []);
        const fData = await fRes.json().catch(() => []);
        if (cancelled) return;
        setWorkers(Array.isArray(wData) ? wData : []);
        setWerkzaamheden(Array.isArray(kData) ? kData : []);
        setFunctiegroepen(Array.isArray(fData) ? fData : []);
        const oList = Array.isArray(oData) ? oData : [];
        setOpdrachtgevers(oList);
        // Default to first opdrachtgever (id=1 / Entice) when creating new contract and none set
        if (!isEdit && oList.length > 0) {
          setForm((p) => p.opdrachtgeverId ? p : { ...p, opdrachtgeverId: String(oList[0].id) });
        }

        // If editing and the saved werkzaamheden doesn't match any active option, switch to 'other'
        if (isEdit && contract?.werkzaamheden) {
          const labels = (Array.isArray(kData) ? kData : []).map((w) => w.label);
          if (!labels.includes(contract.werkzaamheden)) {
            setWerkzaamhedenMode('other');
          }
        }
      } catch {
        if (!cancelled) {
          setWorkers([]);
          setWerkzaamheden([]);
          setOpdrachtgevers([]);
          setFunctiegroepen([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, contract?.werkzaamheden]);

  const workerOptions = useMemo(
    () => workers.map((w) => {
      const naam = `${w.firstName || ''} ${w.lastName || ''}`.trim() || w.name || `Worker #${w.id}`;
      const bedrijf = w.bedrijfsnaam ? ` — ${w.bedrijfsnaam}` : '';
      return { value: String(w.id), label: naam + bedrijf };
    }),
    [workers]
  );

  const selectedWorker = useMemo(
    () => workers.find((w) => String(w.id) === String(form.workerId)) || null,
    [workers, form.workerId]
  );

  const selectedWorkerFunctiegroep = useMemo(() => {
    if (!selectedWorker || !selectedWorker.functiegroepId) return null;
    return functiegroepen.find((f) => String(f.id) === String(selectedWorker.functiegroepId)) || null;
  }, [selectedWorker, functiegroepen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setError('');
  };

  const handleWorkerChange = (val) => {
    setForm((p) => ({ ...p, workerId: val }));
    setError('');
  };

  const handleWerkzaamhedenSelectChange = (e) => {
    const value = e.target.value;
    if (value === '__other__') {
      setWerkzaamhedenMode('other');
      // keep existing werkzaamheden text so user can edit it
      return;
    }
    setWerkzaamhedenMode('select');
    setForm((p) => ({ ...p, werkzaamheden: value }));
    setError('');
  };

  const handleWerkzaamhedenTextareaChange = (e) => {
    setForm((p) => ({ ...p, werkzaamheden: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.workerId) {
      setError('Worker is verplicht');
      return;
    }
    setSaving(true);

    const payload = {
      worker_id:        parseInt(form.workerId, 10),
      opdrachtgever_id: form.opdrachtgeverId ? parseInt(form.opdrachtgeverId, 10) : null,
      project_naam:  form.project_naam.trim(),
      werkzaamheden: form.werkzaamheden.trim(),
      datum_in:      form.datum_in   || null,
      datum_uit:     form.datum_uit  || null,
      tekendatum:    form.tekendatum || null,
    };

    try {
      const url    = isEdit ? `/api/zzp/contracts/${contract.id}` : '/api/zzp/contracts';
      const method = isEdit ? 'PUT' : 'POST';
      const res    = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Opslaan mislukt');
      }
      onSaved && onSaved();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  // Werkzaamheden select current value (only show if current text matches an option)
  const werkzaamhedenLabels = werkzaamheden.map((w) => w.label);
  const werkzaamhedenSelectValue =
    werkzaamhedenMode === 'other'
      ? '__other__'
      : (werkzaamhedenLabels.includes(form.werkzaamheden) ? form.werkzaamheden : '');

  const formatRate = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = parseFloat(v);
    if (isNaN(n)) return '—';
    return `€${n.toFixed(2)}`;
  };

  return (
    <div className="zzp-modal-overlay" onClick={onClose}>
      <div className="zzp-modal-content cfm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="zzp-modal-header">
          <h3>{isEdit ? 'Contract bewerken' : 'Nieuw Contract'}</h3>
          <button className="zzp-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="zzp-form">
          <div className="zzp-form-group">
            <label>Worker *</label>
            <SearchableSelect
              options={workerOptions}
              value={String(form.workerId || '')}
              onChange={handleWorkerChange}
              placeholder="Kies een worker..."
              searchPlaceholder="Zoek worker..."
            />
            {selectedWorker && (
              <>
                <div className="cfm-hint">
                  Tarieven worden automatisch toegepast vanuit de functiegroep van de medewerker.
                </div>
                {selectedWorkerFunctiegroep ? (
                  <div className="cfm-rates-preview">
                    <div className="cfm-rates-preview-title">
                      Tarieven voor {selectedWorkerFunctiegroep.naam}:
                    </div>
                    <div className="cfm-rates-preview-grid">
                      {RATE_LABELS.map(({ key, label }) => (
                        <div key={key} className="cfm-rates-preview-item">
                          <span className="cfm-rates-preview-label">{label.split(' — ')[0]}</span>
                          <span className="cfm-rates-preview-value">{formatRate(selectedWorkerFunctiegroep[key])}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="zzp-form-error" style={{ marginTop: 8 }}>
                    ⚠️ Deze medewerker heeft geen functiegroep — tarieven zullen leeg blijven in het contract.
                  </div>
                )}
              </>
            )}
          </div>

          <div className="zzp-form-group">
            <label htmlFor="opdrachtgeverId">Opdrachtgever</label>
            <select
              id="opdrachtgeverId"
              name="opdrachtgeverId"
              value={form.opdrachtgeverId}
              onChange={handleChange}
            >
              <option value="">— Kies opdrachtgever —</option>
              {opdrachtgevers.map((o) => (
                <option key={o.id} value={o.id}>{o.naam}</option>
              ))}
            </select>
          </div>

          <div className="zzp-form-group">
            <label htmlFor="project_naam">Project naam</label>
            <input
              id="project_naam"
              type="text"
              name="project_naam"
              placeholder="Project Keppel Verolme"
              value={form.project_naam}
              onChange={handleChange}
            />
          </div>

          <div className="zzp-form-group">
            <label htmlFor="werkzaamheden_select">Werkzaamheden</label>
            <select
              id="werkzaamheden_select"
              value={werkzaamhedenSelectValue}
              onChange={handleWerkzaamhedenSelectChange}
            >
              <option value="">— Kies werkzaamheden —</option>
              {werkzaamheden.map((w) => (
                <option key={w.id} value={w.label}>{w.label}</option>
              ))}
              <option value="__other__">Anders…</option>
            </select>
            {werkzaamhedenMode === 'other' && (
              <textarea
                id="werkzaamheden"
                name="werkzaamheden"
                rows={4}
                placeholder="Omschrijving van de werkzaamheden..."
                value={form.werkzaamheden}
                onChange={handleWerkzaamhedenTextareaChange}
                style={{ marginTop: 8 }}
              />
            )}
          </div>

          <div className="zzp-form-row cfm-row-3">
            <div className="zzp-form-group">
              <label htmlFor="datum_in">Datum in</label>
              <input
                id="datum_in"
                type="date"
                name="datum_in"
                value={form.datum_in}
                onChange={handleChange}
              />
            </div>
            <div className="zzp-form-group">
              <label htmlFor="datum_uit">Datum uit</label>
              <input
                id="datum_uit"
                type="date"
                name="datum_uit"
                value={form.datum_uit}
                onChange={handleChange}
              />
            </div>
            <div className="zzp-form-group">
              <label htmlFor="tekendatum">Tekendatum</label>
              <input
                id="tekendatum"
                type="date"
                name="tekendatum"
                value={form.tekendatum}
                onChange={handleChange}
              />
            </div>
          </div>

          {error && <div className="zzp-form-error">{error}</div>}

          <div className="zzp-form-actions">
            <button type="button" className="zzp-btn-cancel" onClick={onClose}>
              Annuleren
            </button>
            <button type="submit" className="zzp-btn-submit" disabled={saving}>
              {saving ? 'Opslaan...' : (isEdit ? 'Opslaan' : 'Aanmaken')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
